/* ============================================================
 * Iter EXP-2A — Unified Readiness Banner
 *
 * Single visual language for every "NOT READY / NEEDS ACTION /
 * READY" verdict across the platform. Reused by Survey Control,
 * Calibration Studio, Plan Review, Uploads, Field Positioning,
 * Imports, Alignments.
 *
 * Purpose:
 *   The operator must NEVER hunt for readiness.
 *   Every workflow surfaces a single big card with state, the
 *   missing items, and a one-click CTA per missing item.
 *
 * State precedence (operator's mental model):
 *   "ready"        — green CheckCircle2, success copy, no items
 *   "needs-action" — amber AlertTriangle, list of items + CTAs
 *   "not-ready"    — red AlertCircle, blocked copy + reset CTA
 *
 * Usage:
 *   <ReadinessBanner
 *     state="needs-action"
 *     title="3 items before field work"
 *     subtitle="Field crews need a complete coordinate system..."
 *     items={[
 *       { key: "settings", label: "Coordinate system not set",
 *         ctaLabel: "Open Setup Wizard", onClick: openWizard },
 *       ...
 *     ]}
 *     testid="readiness-verdict"
 *   />
 * ============================================================ */

import React from "react";
import { CheckCircle2, AlertTriangle, AlertCircle, ChevronRight } from "lucide-react";

const PALETTE = {
  ready: {
    color: "var(--fop-success, #22c55e)",
    bg: "rgba(34,197,94,0.10)",
    Icon: CheckCircle2,
  },
  "needs-action": {
    color: "var(--fop-warning, #f59e0b)",
    bg: "rgba(245,158,11,0.10)",
    Icon: AlertTriangle,
  },
  "not-ready": {
    color: "var(--fop-danger, #ef4444)",
    bg: "rgba(239,68,68,0.10)",
    Icon: AlertCircle,
  },
};

export default function ReadinessBanner({
  state = "needs-action",
  title,
  subtitle,
  items = [],
  testid = "readiness-banner",
  compact = false,
}) {
  const p = PALETTE[state] || PALETTE["needs-action"];
  const Icon = p.Icon;

  return (
    <div
      className={`rounded-xl border-2 ${compact ? "px-4 py-3" : "px-5 py-4"}`}
      style={{ borderColor: p.color, background: p.bg }}
      data-testid={testid}
      data-readiness-state={state}
    >
      <div className="flex items-center gap-4">
        <div
          className={`${compact ? "w-9 h-9" : "w-12 h-12"} rounded-full flex items-center justify-center flex-shrink-0`}
          style={{ background: p.color }}
        >
          <Icon size={compact ? 22 : 28} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-display font-bold leading-none text-[var(--fop-text)] ${compact ? "text-base" : "text-xl"}`}>
            {title}
          </div>
          {subtitle && (
            <div className={`text-[var(--fop-text-2)] mt-1 ${compact ? "text-xs" : "text-sm"}`}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {items.length > 0 && state !== "ready" && (
        <ul
          className={`grid grid-cols-1 ${
            items.length >= 3 ? "sm:grid-cols-3"
            : items.length === 2 ? "sm:grid-cols-2"
            : "sm:grid-cols-1"
          } gap-2 ${compact ? "mt-3" : "mt-4"}`}
          data-testid={`${testid}-items`}
        >
          {items.map((it) => (
            <li
              key={it.key}
              className="rounded-lg border border-[var(--fop-border-2)] bg-[var(--fop-surface)]/80 px-3 py-2.5 flex items-center justify-between gap-2"
              data-testid={`${testid}-item-${it.key}`}
            >
              <div className="text-[13px] font-bold text-[var(--fop-text)] leading-tight">
                {it.label}
              </div>
              {it.onClick && (
                <button
                  type="button"
                  className="text-[12px] font-mono uppercase tracking-wider font-bold text-[var(--fop-primary)] hover:text-[var(--fop-primary-hover)] flex items-center gap-1 flex-shrink-0"
                  onClick={it.onClick}
                  data-testid={`${testid}-cta-${it.key}`}
                >
                  {it.ctaLabel || "Fix"} <ChevronRight size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
